import {
    createRoom,
    getRoom,
    addCandidate,
    saveOffer,
    saveAnswer,
    listenToRoom,
    registerPeerConnectionListeners
} from './signaling';

const servers = {
    iceServers: [
        {
            urls: ['stun:stun1.l.google.com:19302', 'stun:stun2.l.google.com:19302'],
        },
    ],
    iceCandidatePoolSize: 10,
};

export class WebRTCService {
    constructor() {
        this.pc = null;
        this.localStream = null;
        this.remoteStream = null;
        this.roomId = null;
        this.unsubscribe = null;
    }

    async init(localStream, onRemoteStream) {
        this.localStream = localStream;
        this.pc = new RTCPeerConnection(servers);

        // Push tracks
        localStream.getTracks().forEach((track) => {
            this.pc.addTrack(track, localStream);
        });

        // Pull tracks
        this.remoteStream = new MediaStream();
        this.pc.ontrack = (event) => {
            event.streams[0].getTracks().forEach((track) => {
                this.remoteStream.addTrack(track);
            });
            if (onRemoteStream) onRemoteStream(this.remoteStream);
        };
    }

    async createCall(roomId) {
        this.roomId = roomId;
        await createRoom(roomId);

        this.pc.onicecandidate = (event) => {
            if (event.candidate) {
                addCandidate(roomId, event.candidate, 'callerCandidates');
            }
        };

        const offer = await this.pc.createOffer();
        await this.pc.setLocalDescription(offer);

        const roomWithOffer = {
            offer: {
                type: offer.type,
                sdp: offer.sdp,
            },
        };

        await saveOffer(roomId, roomWithOffer.offer);

        // Listen for answer
        this.unsubscribe = listenToRoom(roomId, async (data) => {
            if (!this.pc.currentRemoteDescription && data?.answer) {
                const rtcSessionDescription = new RTCSessionDescription(data.answer);
                await this.pc.setRemoteDescription(rtcSessionDescription);
            }
        });

        registerPeerConnectionListeners(this.pc, roomId, 'calleeCandidates');
    }

    async joinCall(roomId) {
        this.roomId = roomId;
        const roomData = await getRoom(roomId);

        if (roomData?.offer) {
            this.pc.onicecandidate = (event) => {
                if (event.candidate) {
                    addCandidate(roomId, event.candidate, 'calleeCandidates');
                }
            };

            await this.pc.setRemoteDescription(new RTCSessionDescription(roomData.offer));
            const answer = await this.pc.createAnswer();
            await this.pc.setLocalDescription(answer);

            const roomWithAnswer = {
                answer: {
                    type: answer.type,
                    sdp: answer.sdp,
                },
            };

            await saveAnswer(roomId, roomWithAnswer.answer);

            registerPeerConnectionListeners(this.pc, roomId, 'callerCandidates');
        }
    }

    async hangup() {
        if (this.unsubscribe) this.unsubscribe();
        if (this.pc) this.pc.close();
        this.pc = null;
    }
}
